/**
 * Hooks da suíte (SPEC-v2 2.3, 2.4): resumo do lançador, sino, sair e o provider raiz. Usados pelo
 * lançador e pelos shells das 3 áreas; nenhuma área importa outra (V2 9.3).
 */
import type { AreaSummary, SuiteNotification } from '@rota/contracts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { sessionStore, useSession } from '@/data/auth/session';
import { getSuite } from '@/data/provider';

export function useSuite() {
  return getSuite();
}

export interface SummaryState {
  data: AreaSummary[] | null;
  state: 'loading' | 'ready' | 'error';
}

/** `suite.summary()`: uma chamada só, < 50 ms no mock (2.3). */
export function useSuiteSummary(): SummaryState {
  const session = useSession();
  const [s, setS] = useState<SummaryState>({ data: null, state: 'loading' });
  useEffect(() => {
    if (!session) return;
    let live = true;
    getSuite()
      .summary()
      .then(
        (data) => live && setS({ data, state: 'ready' }),
        () => live && setS({ data: null, state: 'error' }),
      );
    return () => {
      live = false;
    };
  }, [session]);
  return s;
}

/** Itens do sino da suíte (B e C, e o Painel por fixture até o Painel subir). */
export function useSuiteNotifications(): {
  items: SuiteNotification[];
  markAll(): void;
  markRead(id: string): void;
} {
  const session = useSession();
  const [items, setItems] = useState<SuiteNotification[]>([]);
  useEffect(() => {
    if (!session) return;
    setItems([]);
    return getSuite().notifications((n) =>
      setItems((prev) => [...prev.filter((x) => x.id !== n.id), n].sort((a, b) => b.at - a.at)),
    );
  }, [session]);
  const markAll = useCallback(() => {
    setItems((prev) => {
      void getSuite().markRead(prev.map((p) => p.id));
      return prev.map((p) => ({ ...p, read: true }));
    });
  }, []);
  const markRead = useCallback((id: string) => {
    void getSuite().markRead([id]);
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, read: true } : p)));
  }, []);
  return useMemo(() => ({ items, markAll, markRead }), [items, markAll, markRead]);
}

/** "Sair" (SUI-13): encerra a sessão e volta a `/entrar`, limpando o estado em memória. */
export function useSignOut(): () => Promise<void> {
  return useCallback(async () => {
    await getSuite().auth.signOut();
    sessionStore.signedOut();
    // Recarrega para zerar caches e o worker do Painel (nada da sessão anterior fica na memória).
    window.location.assign('/entrar');
  }, []);
}
